// Runtime side of the round-32 product metadata rules.
// The rules themselves live in scripts/product-meta.mjs so the prerendered head
// (what crawlers fetch) and the hydrated head (what a rendering crawler ends up
// with) are produced by the same code and can never disagree.
import { useEffect, useState } from "react";
import {
  buildProductDescription,
  buildProductTitle,
  buildQualifiedProductTitle,
  SUFFIX,
  type MetaProduct,
} from "../../scripts/product-meta.mjs";
import { FREE_SHIPPING_THRESHOLD } from "./shipping";

export function productHeadTitle(
  title: string | null | undefined,
  qualifier?: string | null,
  productType?: string | null,
): string {
  return buildQualifiedProductTitle(title, qualifier, productType).title;
}

/**
 * The product's on-page name: the head title without " — LoftLoom". The
 * prerendered H1 and JSON-LD use exactly this, so the hydrated H1, breadcrumb and
 * Product name must too (cleanProductTitle renamed a TV stand "Shelving").
 */
export function productPageName(headTitle: string): string {
  return headTitle.endsWith(SUFFIX) ? headTitle.slice(0, -SUFFIX.length) : headTitle;
}

export function productHeadDescription(product: MetaProduct): string {
  return buildProductDescription(product, { freeShippingThreshold: FREE_SHIPPING_THRESHOLD });
}

/**
 * Duplicate → representative map, generated at build time by
 * scripts/analyze-duplicates.mjs. Fetched once and cached; while it is loading
 * the page keeps the self-canonical it already had.
 */
let cache: Record<string, string> | null = null;
let pending: Promise<Record<string, string>> | null = null;

function loadCanonicals(): Promise<Record<string, string>> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = fetch("/product-canonicals.json")
      .then((res) => (res.ok ? (res.json() as Promise<unknown>) : ({} as unknown)))
      .then((data) => {
        cache =
          data && typeof data === "object" ? (data as Record<string, string>) : {};
        return cache;
      })
      .catch(() => {
        cache = {};
        return cache;
      });
  }
  return pending;
}

/** Returns the handle this product should canonicalise to (itself by default). */
export function useCanonicalHandle(handle: string | undefined): string | undefined {
  const [resolved, setResolved] = useState<string | undefined>(handle ? cache?.[handle] ?? handle : handle);

  useEffect(() => {
    if (!handle) return;
    let active = true;
    loadCanonicals().then((map) => {
      if (active) setResolved(map[handle] ?? handle);
    });
    return () => {
      active = false;
    };
  }, [handle]);

  return resolved;
}

/**
 * Round 36. Handle → the single differentiating title qualifier, generated at
 * build time by scripts/analyze-titles.mjs. Fetched once and cached so the
 * hydrated head says exactly what the prerendered head and <h1> say.
 */
let qualifierCache: Record<string, string> | null = null;
let qualifierPending: Promise<Record<string, string>> | null = null;

function loadTitleQualifiers(): Promise<Record<string, string>> {
  if (qualifierCache) return Promise.resolve(qualifierCache);
  if (!qualifierPending) {
    qualifierPending = fetch("/product-title-qualifiers.json")
      .then((res) => (res.ok ? (res.json() as Promise<unknown>) : ({} as unknown)))
      .then((data) => {
        qualifierCache = data && typeof data === "object" ? (data as Record<string, string>) : {};
        return qualifierCache;
      })
      .catch(() => {
        qualifierCache = {};
        return qualifierCache;
      });
  }
  return qualifierPending;
}

/**
 * The head title for a product, with its qualifier once the map has loaded.
 * `productType` must be passed: the prerender builds the title with it, and the
 * two heads have to agree.
 */
export function useProductHeadTitle(
  handle: string | undefined,
  title: string | null | undefined,
  productType?: string | null,
): string {
  const [qualifier, setQualifier] = useState<string | undefined>(handle ? qualifierCache?.[handle] : undefined);

  useEffect(() => {
    if (!handle) return;
    let active = true;
    loadTitleQualifiers().then((map) => {
      if (active) setQualifier(map[handle]);
    });
    return () => {
      active = false;
    };
  }, [handle]);

  return productHeadTitle(title, qualifier, productType);
}
